import {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {motion} from 'framer-motion';
import {useDropDown} from '../../../../hooks';
import {Label} from '../../../atoms/label';
import {FieldWrapper} from '../../../atoms/field-wrapper';
import {Helper} from '../../../atoms/helper';
import {
  Wrapper,
  DropdownWrapper,
  DropWrapper,
  DropdownHandler,
  HandlerWrapper,
  ArrowIconWrapper,
  OptionsWrapper,
  GroupArrowWrapper,
  SelectionFooter,
  FooterAction,
  FlatGroupPickerBadge,
  FlatGroupPickerBody,
  FlatGroupPickerDivider,
  FlatGroupPickerLabel,
  FlatGroupPickerOption,
  FlatGroupPickerOptions,
} from './styled';
import {SearchBox} from '../../search-box';
import {Checkbox} from '../../../atoms/checkbox';
import {HorizontalSpacer, VerticalSpacer} from '../../../atoms/spacer';
import {Flex, FlexRow} from '@ui/style/styles';
import {Groups} from '../../groups';
import {GreenWhitePlusIcon} from '@ui/atoms/icons/greenWhitePlus';
import {Body1, Body2, CTA} from '../../../atoms/typography';
import {ArrowHeadDownIcon} from '../../../atoms/icons/arrow-head-down';
import {
  MultiOptionsSelectFieldGroupProps,
  SelectOptionItem,
  GroupedSelectOption,
} from './types';
import {MagnifyingGlassIcon} from '../../../atoms/icons';

interface MultiGroupProps {
  option: GroupedSelectOption;
  disabled?: boolean;
  selectedOptions: SelectOptionItem[];
  theme?: string;
  removeCheckbox?: boolean;
  handleSelectAllOptions: (
    options: SelectOptionItem[],
    addOptions: boolean,
  ) => void;
  handleChange: (option: SelectOptionItem) => void;
  enableSelectAll?: boolean;
}

export const MultiOptionsSelectFieldGroup = forwardRef(
  (
    {
      label,
      state,
      helper,
      placeholder,
      options = [],
      onChange,
      theme,
      showSelections,
      defaultValue,
      customOption,
      onClickCustomOption,
      groupDisplayLength,
      showSearchIcon,
      maxSelection,
      singleLabel,
      height,
      enableSelectAll,
      customOptionName,
      handleOnClick,
      removeCheckbox,
      disabled,
      searchPlaceholder,
      performance,
      stagedSelection,
      showSelectedOptionsInMenu,
      showSelectionCancelIcon = true,
      showSelectionFooter,
      showSelectionAction = true,
      selectionActionLabel = 'Save changes',
      flatGroupPicker,
      compact,
    }: MultiOptionsSelectFieldGroupProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const {handleOpen, ref: dropRef, open, handleClose} = useDropDown();

    const [searchValue, setSearchValue] = useState('');
    const [selectedOptions, setSelectedOptions] = useState<SelectOptionItem[]>(
      defaultValue || [],
    );
    const [committedOptions, setCommittedOptions] = useState<
      SelectOptionItem[]
    >(defaultValue || []);

    const filterOption = useMemo(() => {
      const baseOptions = options
        .map((option) => ({
          ...option,
          data: showSelectedOptionsInMenu
            ? option.data
            : option.data.filter(
                (group) =>
                  !selectedOptions.some(
                    (selected) => selected.value === group.value,
                  ),
              ),
        }))
        .filter((option) => option.data.length > 0);

      if (!searchValue) return baseOptions;

      return baseOptions
        .map((option) => ({
          ...option,
          data: option.data.filter((group) =>
            group.label.toLowerCase().includes(searchValue.toLowerCase()),
          ),
        }))
        .filter((option) => option.data.length > 0);
    }, [options, searchValue, selectedOptions, showSelectedOptionsInMenu]);

    useEffect(() => {
      if (defaultValue) {
        setSelectedOptions(defaultValue);
        setCommittedOptions(defaultValue);
      }
    }, [defaultValue]);

    useEffect(() => {
      if (stagedSelection && open) {
        setSelectedOptions(committedOptions);
      }
    }, [committedOptions, open, stagedSelection]);

    const updateSelections = useCallback(
      (nextOptions: SelectOptionItem[]) => {
        setSelectedOptions(nextOptions);
        if (!stagedSelection) {
          setCommittedOptions(nextOptions);
          onChange?.(nextOptions);
        }
      },
      [onChange, stagedSelection],
    );

    const commitSelections = useCallback(() => {
      setCommittedOptions(selectedOptions);
      onChange?.(selectedOptions);
      handleClose();
    }, [handleClose, onChange, selectedOptions]);

    const clearSelections = useCallback(() => {
      setSelectedOptions([]);
      if (!stagedSelection) {
        setCommittedOptions([]);
        onChange?.([]);
      }
    }, [onChange, stagedSelection]);

    const handleChange = useCallback(
      (option: SelectOptionItem) => {
        const alreadySelected = selectedOptions.some(
          (selected) => selected.value === option.value,
        );
        const updatedSelectedOptions = alreadySelected
          ? selectedOptions.filter((opt) => opt.value !== option.value)
          : [...selectedOptions, option];

        updateSelections(updatedSelectedOptions);
      },
      [selectedOptions, updateSelections],
    );

    const handleSelectAllOptions = useCallback(
      (groupOptions: SelectOptionItem[], addOptions: boolean) => {
        const updatedSelectedOptions = addOptions
          ? selectedOptions.filter((selectedOption) =>
              groupOptions.every((opt) => opt.value !== selectedOption.value),
            )
          : [
              ...selectedOptions,
              ...groupOptions.filter((option) =>
                selectedOptions.every(
                  (selected) => selected.value !== option.value,
                ),
              ),
            ];

        updateSelections(updatedSelectedOptions);
      },
      [selectedOptions, updateSelections],
    );

    const handleCancelFx = useCallback(
      (value: string) => {
        const displayedOptions = open ? selectedOptions : committedOptions;
        const updatedSelectedOptions = displayedOptions.filter(
          (option) => option.value !== value,
        );
        setSelectedOptions(updatedSelectedOptions);
        setCommittedOptions(updatedSelectedOptions);
        onChange?.(updatedSelectedOptions);
      },
      [committedOptions, onChange, open, selectedOptions],
    );

    useEffect(() => {
      handleOnClick?.(open);
    }, [open, handleOnClick]);

    const visibleSelections =
      stagedSelection && !open ? committedOptions : selectedOptions;
    const hasSelections = visibleSelections.length > 0;
    const filteredGroups = filterOption.filter((opt) => opt.data.length > 0);
    const flatGroupOptions = filteredGroups.flatMap((groupType) =>
      groupType.data.map((option) => ({
        groupType: groupType.name,
        option,
      })),
    );
    const isSelectionCapReached =
      typeof maxSelection === 'number' &&
      selectedOptions.length >= maxSelection;

    const handleGroupOptionClick = useCallback(
      (option: SelectOptionItem) => {
        const isSelected = selectedOptions.some(
          (selected) => selected.value === option.value,
        );
        if (isSelectionCapReached && !isSelected) return;
        handleChange(option);
      },
      [handleChange, isSelectionCapReached, selectedOptions],
    );

    return (
      <FieldWrapper margin ref={ref}>
        <Wrapper>
          <Label aria-label="label">{label}</Label>
          <DropWrapper ref={dropRef}>
            <DropdownHandler
              open={open}
              onClick={handleOpen}
              height={height}
              $compact={compact}
              performance={performance}
              disabled={disabled}>
              <HandlerWrapper>
                <OptionsWrapper disabled={disabled}>
                  {(hasSelections || singleLabel) &&
                  showSelections !== false ? (
                    singleLabel ? (
                      <Body1>{singleLabel}</Body1>
                    ) : (
                      <Groups
                        total={visibleSelections.length}
                        items={visibleSelections.map((item) => ({
                          id: String(item.value),
                          name: item.label,
                          value: item.value,
                        }))}
                        showCancelIcon={!disabled && showSelectionCancelIcon}
                        groupDisplayLength={groupDisplayLength}
                        disabled={disabled}
                        cancelFx={!disabled ? handleCancelFx : undefined}
                      />
                    )
                  ) : (
                    <p className="greyOut">{placeholder}</p>
                  )}
                </OptionsWrapper>
                <ArrowIconWrapper disabled={disabled} $compact={compact}>
                  {showSearchIcon ? (
                    <MagnifyingGlassIcon />
                  ) : (
                    <motion.span
                      initial={{rotate: 0, marginTop: '0px'}}
                      animate={{
                        rotate: open ? 180 : 0,
                        marginTop: open ? '-6px' : '0px',
                      }}
                      transition={{
                        duration: 0.3,
                      }}
                      key="multi-select-group-toggled">
                      <ArrowHeadDownIcon />
                    </motion.span>
                  )}
                </ArrowIconWrapper>
              </HandlerWrapper>
            </DropdownHandler>

            {open && (
              <div style={{position: 'relative'}}>
                <DropdownWrapper $flatGroupPicker={flatGroupPicker}>
                  {flatGroupPicker ? (
                    <FlatGroupPickerBody>
                      <SearchBox
                        placeholder={searchPlaceholder}
                        onChange={(event) => setSearchValue(event.target.value)}
                        style={{height: '44px'}}
                      />
                      <FlatGroupPickerDivider />
                      <FlatGroupPickerOptions
                        role="listbox"
                        aria-multiselectable>
                        {flatGroupOptions.length > 0 ? (
                          flatGroupOptions.map(({groupType, option}) => {
                            const selected = selectedOptions.some(
                              (item) => item.value === option.value,
                            );
                            return (
                              <FlatGroupPickerOption
                                key={`${groupType}-${option.value}`}
                                role="option"
                                aria-selected={selected}
                                selected={selected}
                                onClick={(event) => {
                                  if (
                                    (event.target as HTMLElement).closest(
                                      'input',
                                    )
                                  ) {
                                    return;
                                  }
                                  handleGroupOptionClick(option);
                                }}>
                                <Checkbox
                                  kind="import"
                                  checked={selected}
                                  onChange={() =>
                                    handleGroupOptionClick(option)
                                  }
                                />
                                <FlatGroupPickerLabel>
                                  {option.label}
                                </FlatGroupPickerLabel>
                                <FlatGroupPickerBadge>
                                  {groupType}
                                </FlatGroupPickerBadge>
                              </FlatGroupPickerOption>
                            );
                          })
                        ) : (
                          <Body1 kind="textDark">No group found</Body1>
                        )}
                      </FlatGroupPickerOptions>
                    </FlatGroupPickerBody>
                  ) : (
                    <div style={{padding: '10px', zIndex: 5}}>
                      <SearchBox
                        placeholder={searchPlaceholder}
                        onChange={(event) => setSearchValue(event.target.value)}
                        style={{height: '44px', marginBottom: '16px'}}
                      />
                      <div className="overflow-hidden">
                        <div className="max-h-[350px] overflow-auto">
                          {filterOption.length > 0 &&
                          filteredGroups.length > 0 ? (
                            filteredGroups.map((option, index) => (
                              <div
                                style={{
                                  marginTop: index === 0 ? '12px' : '4px',
                                }}
                                key={option.name}>
                                <MultiGroup
                                  theme={theme}
                                  option={option}
                                  handleChange={handleGroupOptionClick}
                                  enableSelectAll={enableSelectAll}
                                  handleSelectAllOptions={
                                    handleSelectAllOptions
                                  }
                                  removeCheckbox={removeCheckbox}
                                  disabled={disabled}
                                  selectedOptions={selectedOptions}
                                />
                              </div>
                            ))
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '12px',
                              }}>
                              <Body1 kind="textDark">No group found</Body1>
                            </div>
                          )}
                        </div>
                      </div>

                      {customOption && (
                        <FlexRow
                          onClick={() => {
                            onClickCustomOption?.();
                            handleClose();
                          }}
                          style={{marginTop: '6px', cursor: 'pointer'}}>
                          <GreenWhitePlusIcon />{' '}
                          <HorizontalSpacer size="12px" />{' '}
                          <CTA kind="textDark" style={{fontWeight: 600}}>
                            {customOptionName || 'Manage groups'}
                          </CTA>
                        </FlexRow>
                      )}
                    </div>
                  )}
                  {showSelectionFooter && (
                    <SelectionFooter $flatGroupPicker={flatGroupPicker}>
                      <FooterAction type="button" onClick={clearSelections}>
                        Clear all
                      </FooterAction>
                      {showSelectionAction && (
                        <FooterAction
                          type="button"
                          primary
                          onClick={commitSelections}>
                          {selectionActionLabel}
                        </FooterAction>
                      )}
                    </SelectionFooter>
                  )}
                </DropdownWrapper>
              </div>
            )}
          </DropWrapper>
          <Helper aria-label="helper" state={state} children={helper} />
        </Wrapper>
      </FieldWrapper>
    );
  },
);

export const MultiGroup = ({
  option,
  disabled,
  selectedOptions,
  theme,
  removeCheckbox,
  handleSelectAllOptions,
  handleChange,
  enableSelectAll,
}: MultiGroupProps) => {
  const isUsers = theme === 'users';
  const [showOptions, setshowOptions] = useState(isUsers);
  const selectedIds = useMemo(() => selectedOptions.map((item) => item.value), [
    selectedOptions,
  ]);

  const isAllSelected = option?.data?.every((groupOption) =>
    selectedIds.includes(groupOption.value),
  );

  return (
    <div style={{margin: '0 2px'}}>
      {option?.data?.length > 0 && (
        <>
          {isUsers && (
            <div className="bg-[#FAFAFA] py-2 px-2 rounded-lg ">
              <Body2
                kind={!showOptions ? 'textBody' : 'textDark'}
                weight="bold">
                {option?.name}
              </Body2>
            </div>
          )}
          {!isUsers && (
            <div
              onClick={() => setshowOptions(!showOptions)}
              className="flex justify-between cursor-pointer py-[2px] px-[3px]">
              <Flex>
                {enableSelectAll && (
                  <Checkbox
                    kind="import"
                    checked={isAllSelected}
                    onChange={() => {
                      setshowOptions(true);
                      handleSelectAllOptions(option?.data, isAllSelected);
                    }}
                  />
                )}
                <Body2
                  kind={!showOptions ? 'textBody' : 'textDark'}
                  style={showOptions ? {fontWeight: 500} : {fontWeight: 400}}>
                  {option?.name}
                </Body2>
              </Flex>
              <GroupArrowWrapper disabled={disabled}>
                <motion.span
                  initial={{rotate: 0}}
                  animate={{
                    rotate: !disabled && showOptions ? 180 : 0,
                    marginTop: !disabled && showOptions ? '-6px' : '0px',
                  }}
                  transition={{
                    duration: 0.3,
                  }}
                  key="multi-group-arrow">
                  <ArrowHeadDownIcon />
                </motion.span>
              </GroupArrowWrapper>
            </div>
          )}
        </>
      )}
      {showOptions && option?.data?.length > 0 && (
        <>
          <div
            style={{
              background: isUsers ? 'white' : '#FAFAFA',
              borderRadius: '0px 0px 8px 8px',
              borderTop: isUsers ? '0' : '1px solid #ededf2',
            }}>
            {option?.data?.map((item) => (
              <div
                key={item.value}
                style={{
                  display: 'flex',
                  padding: '8px',
                  cursor: 'pointer',
                }}
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest('input')) return;
                  handleChange(item);
                }}>
                {removeCheckbox && (
                  <Checkbox
                    kind="import"
                    id={item?.id ? String(item.id) : undefined}
                    checked={selectedOptions?.some(
                      (selected) => selected.value === item.value,
                    )}
                    onChange={() => handleChange(item)}
                  />
                )}

                <Body2 kind="textBody" style={{marginLeft: '8px'}}>
                  {item?.label}
                </Body2>
              </div>
            ))}
          </div>
          <VerticalSpacer size="16px" />
        </>
      )}
    </div>
  );
};
